import React from 'react';
import useCustomerCarriersAndGroups from '@hooks/customer-carrier/customer-carriers-groups';
import {
  CarrierGroupInDTO,
  CarrierOrGroupEnum,
  FindCarrierCarrierOrGroupDTO,
  FindCarrierCarrierOrGroupResponseOutDTO,
} from '@api/ringil-api.v2';
import { MultiSelect } from 'primereact/multiselect';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { toTestIdSlug } from '@utils/testUtils.ts';

interface ComponentProps {
  value: CarrierGroupInDTO[];
  onSelected: (values: FindCarrierCarrierOrGroupResponseOutDTO[]) => void;
  onlyCarriers: boolean;
  isDisabled: boolean;
  placeholder: string;
  testIdBase?: string;
}

export const carriersAndGroupsTemplate = (option: FindCarrierCarrierOrGroupDTO) => {
  return (
    <div className="p-d-flex p-ai-center">
      {option.carrierOrGroup === CarrierOrGroupEnum.CARRIER ? <i className="pi pi-id-card"></i> :
        <i className="pi pi-list"></i>}
      <span className="p-ml-2">{option.name}</span>
    </div>
  );
};

export const carriersTemplate = (option: FindCarrierCarrierOrGroupDTO) => {
  return (
    <div className="p-d-flex p-ai-center">
      <i className="pi pi-id-card"></i>
      <span className="p-ml-2">{option.name}</span>
    </div>
  );
};

export const groupsTemplate = (option: FindCarrierCarrierOrGroupDTO) => {
  return (
    <div className="p-d-flex p-ai-center">
      <i className="pi pi-list"></i>
      <span className="p-ml-2">{option.name}</span>
    </div>
  );
};

export const SelectCarriers: React.FC<ComponentProps> = ({
                                                           value,
                                                           onSelected,
                                                           placeholder,
                                                           onlyCarriers = false,
                                                           isDisabled,
                                                           testIdBase,
                                                         }) => {
  const carriersAndGroups = useCustomerCarriersAndGroups();

  const completeValues = React.useMemo(() => {
    if (!value || !carriersAndGroups.length) return value;

    return value.map(val => {
      const matchedItem = carriersAndGroups.find(item =>
        item.id === val.id &&
        (
          !val.userId
            ? !item.userIds || item.userIds.length === 0
            : item.userIds?.includes(val.userId)
        )
      );
      if (matchedItem) {
        return matchedItem;
      }

      return val;
    });
  }, [value, carriersAndGroups]);

  const selectedCarriersAndGroupsTemplate = (option: FindCarrierCarrierOrGroupDTO) => {
    if (option) {
      return (
        <div className={'p-multiselect-label'}>
          {option.carrierOrGroup === CarrierOrGroupEnum.CARRIER ? <i className="pi pi-id-card"></i> :
            <i className="pi pi-list"></i>}
          <span className="p-ml-1 p-mr-2">{option.name}</span>
        </div>
      );
    }
    return <div className={'p-multiselect-label p-placeholder'}> {placeholder}</div>;
  };

  const itemTemplate = (option: FindCarrierCarrierOrGroupDTO) => (
    <div
      className="p-d-flex p-ai-center"
      data-testid={testIdBase ? `${testIdBase}-${toTestIdSlug(option.name)}` : undefined}
    >
      {option.carrierOrGroup === CarrierOrGroupEnum.CARRIER ? <i className="pi pi-id-card"></i> :
        <i className="pi pi-list"></i>}
      <span className="p-ml-2">{option.name}</span>
    </div>
  );

  const panelHeaderTemplate = (options: any) => {
    const filterElement = React.isValidElement(options.filterElement)
      ? React.cloneElement(options.filterElement, {
        'data-testid': testIdBase ? `${testIdBase}-input` : undefined,
      })
      : options.filterElement;

    return (
      <div className={options.className}>
        {options.checkboxElement}
        {filterElement}
        {options.closeElement}
      </div>
    );
  };


  return (
    <div data-testid={testIdBase}>
      <MultiSelect
        value={completeValues}
        options={carriersAndGroups.filter(t => (onlyCarriers ? t.carrierOrGroup === CarrierOrGroupEnum.CARRIER : true))}
        optionLabel="name"
        filter
        className="multiselect-custom"
        onChange={e => onSelected(e.value)}
        style={{ width: '100%' }}
        disabled={isDisabled}
        itemTemplate={itemTemplate}
        selectedItemTemplate={selectedCarriersAndGroupsTemplate}
        panelHeaderTemplate={panelHeaderTemplate}
        maxSelectedLabels={15}
        placeholder={placeholder}
      />
    </div>
  );
};
